import { useRouter } from 'next/router';
import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IApplicationState } from '@bridebook/web/lib/types';
import { lazyComponent } from '@bridebook/web/lib/utils/lazy/lazy-component';
import { AIAssistantProvider } from './ai-assistant-provider';

const AIAssistantDialog = lazyComponent(
  () => import('@bridebook/web/components/ai-assistant/ai-assistant-dialog'),
);

/**
 * Sticky Provider, transient Dialog:
 *  - Nothing mounts until the user opens the dialog for the first time
 *    (no Provider init, no chat transport, no dialog chunk).
 *  - After the first open, the Provider stays mounted forever — chat state
 *    (messages, in-flight stream) survives close/reopen and route changes
 *    (so back-nav from a venue page restores the conversation).
 *  - The Dialog mounts only while open AND on the search route; navigating
 *    to a venue unmounts it without clearing Redux `isDialogOpen`, so the
 *    browser back button re-opens it at the same conversation state.
 */
const AIAssistantDialogLazy = () => {
  const router = useRouter();
  const isOpen = useSelector<IApplicationState, boolean>((s) => s.aiAssistant.isDialogOpen);
  const [wasOpened, setWasOpened] = useState(false);

  useEffect(() => {
    if (isOpen) setWasOpened(true);
  }, [isOpen]);

  if (!wasOpened) return null;
  const isSearchRoute = router.pathname.startsWith('/[market]/search');
  return (
    <AIAssistantProvider>{isOpen && isSearchRoute && <AIAssistantDialog />}</AIAssistantProvider>
  );
};

export default memo(AIAssistantDialogLazy);
